import { useEffect, useState } from "react";
import { api } from "@/lib/apiClient";
import { DEFAULT_OCCUPANCY_SETTINGS } from "@/lib/occupancySettings";
import { PITCH_USAGE_TYPE_OPTIONS, type PitchUsageType } from "@/lib/pitchUsageType";
import { Card } from "@/components/ui/Card";
import { SettingsSaveRow } from "@/pages/stammdaten/settings/SettingsSaveRow";
import {
  errorMessage,
  type SettingsSectionProps,
  type TenantSettings,
} from "@/pages/stammdaten/settings/tenantSettingsTypes";

function usageTypesFrom(settings: TenantSettings): PitchUsageType[] {
  return settings.occupancy?.dashboard_usage_types ?? DEFAULT_OCCUPANCY_SETTINGS.dashboard_usage_types;
}

export function OccupancySection({ settings, canWrite, onSaved }: SettingsSectionProps) {
  const [usageTypes, setUsageTypes] = useState<PitchUsageType[]>(usageTypesFrom(settings));
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setUsageTypes(usageTypesFrom(settings));
  }, [settings]);

  function toggle(value: PitchUsageType) {
    setUsageTypes((current) => {
      if (current.includes(value)) {
        const next = current.filter((item) => item !== value);
        return next.length > 0 ? next : current;
      }
      return [...current, value];
    });
  }

  async function onSave(e: React.FormEvent) {
    e.preventDefault();
    if (!canWrite) return;
    setSaving(true);
    setError(null);
    setSuccess(false);
    try {
      await api.patch<TenantSettings>("/api/v1/settings/occupancy", {
        dashboard_usage_types: usageTypes,
      });
      setSuccess(true);
      onSaved();
    } catch (err) {
      setError(errorMessage(err, "Belegungs-Einstellungen konnten nicht gespeichert werden"));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card
      title="Dashboard-Belegung"
      description="Welche Platztypen in die Belegungszahlen auf dem Dashboard einfließen (Belegung ist/erwartet, freie Plätze, Auslastung in Prozent). Standardmäßig zählen nur Tourismusplätze."
      bodyClassName="space-y-4"
    >
      <form onSubmit={(e) => void onSave(e)} className="space-y-4">
        <fieldset className="space-y-3" disabled={!canWrite}>
          {PITCH_USAGE_TYPE_OPTIONS.map((option) => (
            <label key={option.value} className="flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                className="mt-1"
                checked={usageTypes.includes(option.value)}
                onChange={() => toggle(option.value)}
                disabled={!canWrite}
              />
              <span>{option.label}</span>
            </label>
          ))}
          <p className="text-xs text-label-tertiary">
            Mindestens ein Platztyp muss ausgewählt sein. Änderungen gelten für die
            Dashboard-Kennzahlen und den Tagesreport.
          </p>
        </fieldset>
        <SettingsSaveRow
          canWrite={canWrite}
          saving={saving}
          success={success}
          successText="Belegungs-Einstellungen gespeichert."
          error={error}
          label="Belegung speichern"
        />
      </form>
    </Card>
  );
}
